import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { formatDateTime } from '../../i18n';
import type { ComboboxOption } from '../../components/Combobox';
import { CustomerCombobox } from '../crm/CustomerCombobox';
import { FormFieldEditor, type FormField } from './FormFieldEditor';
import { useMasterData } from '../masterdata/queries';
import {
  useCreateWebForm,
  useDeleteWebForm,
  useSubmissions,
  useUpdateWebForm,
  useWebForms,
} from './queries';
import { Select } from '../../components/Select';

/**
 * Web-Formulare -- F3-08, K-45.
 *
 * **Der Zweck dieser Seite ist die Einbettung.** Ein Formular anzulegen ist
 * drei Felder; was der Betreiber wirklich braucht, sind die zwei Zeilen fuer
 * die Website des Kunden -- deshalb stehen sie fertig zum Kopieren daneben.
 *
 * Die Einsendungen zeigen **auch die abgewiesenen**. Ohne sie sieht der
 * Betreiber nur, dass keine Tickets mehr kommen; mit ihnen sieht er, woran es
 * liegt: lauter 'honeypot' heisst Roboter, lauter 'too_fast' heisst
 * vermutlich, dass die Seite ein Feld vorausfuellt.
 */
export function WebFormsPage() {
  const { t } = useTranslation();
  const forms = useWebForms();
  const queues = useMasterData('queue');
  const create = useCreateWebForm();
  const update = useUpdateWebForm();
  const remove = useDeleteWebForm();

  const [name, setName] = useState('');
  const [queueId, setQueueId] = useState('');
  const [customer, setCustomer] = useState<ComboboxOption | null>(null);
  const customerId = customer?.id ?? '';
  const [intro, setIntro] = useState('');
  const [origin, setOrigin] = useState('');
  const [showInPortal, setShowInPortal] = useState(false);
  const [fields, setFields] = useState<FormField[]>([]);
  const [showSubmissions, setShowSubmissions] = useState<string | null>(null);

  const submissions = useSubmissions(showSubmissions);

  const snippetFor = (token: string): string =>
    `<div id="itscrm-formular"></div>\n<script src="${window.location.origin}/api/v1/public/forms/${token}/embed.js"></script>`;

  return (
    <>
      <PageHeader title={t('webforms.title')} subtitle={t('webforms.subtitle')} />

      <section className="card">
        <h2>{t('webforms.new')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="form-name">
            {t('masterdata.name')}
          </label>
          <input
            id="form-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="form-queue">
            {t('webforms.queue')}
          </label>
          <Select
            id="form-queue"
            className="field__control"
            value={queueId}
            onChange={(event) => {
              setQueueId(event.target.value);
            }}
          >
            <option value="">{t('macros.chooseValue')}</option>
            {(queues.data ?? []).map((queue) => (
              <option key={queue.id} value={queue.id}>
                {queue.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="form-customer">
            {t('webforms.customer')}
          </label>
          <CustomerCombobox id="form-customer" value={customer} onChange={setCustomer} />
          <span className="field__hint">{t('webforms.customerHint')}</span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="form-intro">
            {t('webforms.intro')}
          </label>
          <input
            id="form-intro"
            className="field__control"
            value={intro}
            onChange={(event) => {
              setIntro(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="form-origin">
            {t('webforms.origin')}
          </label>
          <input
            id="form-origin"
            className="field__control"
            placeholder="https://www.kunde.example"
            value={origin}
            onChange={(event) => {
              setOrigin(event.target.value);
            }}
          />
          <span className="field__hint">{t('webforms.originHint')}</span>
        </div>

        {/* Anfragetyp im Portal -- F4-20, K-105. Getrennt von "aktiv":
            Ein eingebettetes Kontaktformular fragt nach Name und Adresse,
            die im Portal laengst bekannt sind. */}
        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={showInPortal}
            onChange={(event) => {
              setShowInPortal(event.target.checked);
            }}
          />
          {t('webforms.showInPortal')}
        </label>
        <span className="field__hint">{t('webforms.showInPortalHint')}</span>

        <FormFieldEditor fields={fields} onChange={setFields} />

        {create.error ? <ErrorNotice error={create.error} /> : null}

        <div className="row">
          <Button
            variant="primary"
            disabled={name.trim() === '' || queueId === '' || customerId === '' || create.isPending}
            onClick={() => {
              create.mutate(
                {
                  name: name.trim(),
                  queue_id: queueId,
                  customer_id: customerId,
                  intro: intro.trim(),
                  allowed_origins: origin.trim() ? [origin.trim()] : [],
                  is_active: true,
                  show_in_portal: showInPortal,
                  fields,
                },
                {
                  onSuccess: () => {
                    setName('');
                    setIntro('');
                    setOrigin('');
                    setFields([]);
                    setShowInPortal(false);
                  },
                },
              );
            }}
          >
            {t('masterdata.add')}
          </Button>
        </div>
      </section>

      <section className="card">
        <h2>{t('webforms.existing')}</h2>

        {forms.isPending ? <LoadingIndicator /> : null}
        {forms.error ? <ErrorNotice error={forms.error} /> : null}
        {forms.data?.length === 0 ? <p className="table__muted">{t('webforms.none')}</p> : null}

        <ul className="task-list">
          {(forms.data ?? []).map((form) => (
            <li key={form.id} className="task-list__item task-list__item--column">
              <div className="row">
                <strong>{form.name}</strong>
                <Badge tone={form.is_active ? 'success' : 'neutral'}>
                  {form.is_active ? t('automation.active') : t('automation.inactive')}
                </Badge>
                {form.allowed_origins.length > 0 ? (
                  <span className="table__muted">{form.allowed_origins.join(', ')}</span>
                ) : null}
              </div>

              <label className="field__label" htmlFor={`snippet-${form.id}`}>
                {t('webforms.snippet')}
              </label>
              <textarea
                id={`snippet-${form.id}`}
                className="field__control"
                readOnly
                rows={2}
                value={snippetFor(form.public_token)}
                onFocus={(event) => {
                  event.currentTarget.select();
                }}
              />

              <div className="row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    update.mutate({
                      formId: form.id,
                      payload: { is_active: !form.is_active },
                    });
                  }}
                >
                  {form.is_active ? t('automation.deactivate') : t('automation.activate')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowSubmissions(showSubmissions === form.id ? null : form.id);
                  }}
                >
                  {t('webforms.submissions')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(t('masterdata.deleteConfirm'))) {
                      remove.mutate(form.id);
                    }
                  }}
                >
                  {t('masterdata.delete')}
                </Button>
              </div>

              {showSubmissions === form.id ? (
                <ul className="task-list">
                  {(submissions.data ?? []).length === 0 ? (
                    <li className="table__muted">{t('webforms.noSubmissions')}</li>
                  ) : null}
                  {(submissions.data ?? []).map((entry) => (
                    <li key={entry.id} className="task-list__item">
                      <Badge tone={entry.outcome === 'accepted' ? 'success' : 'warning'}>
                        {t(`webforms.outcome.${entry.outcome}`)}
                      </Badge>
                      <span className="table__muted">{formatDateTime(entry.submitted_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
